import * as THREE from "three";

/**
 * Iterative traverse with cycle guard (same behavior as Scene.jsx `safeTraverse`).
 * @param {THREE.Object3D | null | undefined} root
 * @param {(obj: THREE.Object3D) => void | false} visitor return false to stop early
 */
function safeTraverse(root, visitor) {
  if (!root || typeof visitor !== "function") return;
  const stack = [root];
  const seen = new Set();
  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    const res = visitor(obj);
    if (res === false) return;
    const children = obj.children;
    if (Array.isArray(children) && children.length > 0) {
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
    }
  }
}

/**
 * Largest visual extent among root bodies under `modelGroup`, excluding joint viz
 * and inertial helper meshes. Spacing between multiple robots does not inflate the
 * value (unlike `Box3.setFromObject` on the whole model).
 *
 * @param {THREE.Object3D | null | undefined} modelGroup
 * @returns {number}
 */
export function computeLargestRobotDimension(modelGroup) {
  if (!modelGroup) return 0;
  const computeLargestDimFromNode = (node) => {
    const bbox = new THREE.Box3();
    const meshBox = new THREE.Box3();
    let hasRobotGeometryBounds = false;
    safeTraverse(node, (obj) => {
      if (!obj?.isMesh) return;
      let hasJointVizAncestor = false;
      let ancestor = obj.parent;
      while (ancestor) {
        if (ancestor.userData?.isJointVizGroup) {
          hasJointVizAncestor = true;
          break;
        }
        ancestor = ancestor.parent;
      }
      const isJointVizMesh =
        !!obj.userData?.isJointArc ||
        !!obj.userData?.isJointEndpoint ||
        !!obj.userData?.isGlow ||
        !!obj.userData?.isJointPivot ||
        !!obj.userData?.isJointVizGroup ||
        hasJointVizAncestor;
      if (isJointVizMesh) return;
      if (obj.userData?.type === "inertial" || obj.userData?.isInertialHelper) return;
      if (!obj.geometry?.boundingBox && obj.geometry?.computeBoundingBox) {
        obj.geometry.computeBoundingBox();
      }
      if (!obj.geometry?.boundingBox) return;
      meshBox.copy(obj.geometry.boundingBox);
      meshBox.applyMatrix4(obj.matrixWorld);
      if (!hasRobotGeometryBounds) {
        bbox.copy(meshBox);
        hasRobotGeometryBounds = true;
      } else {
        bbox.union(meshBox);
      }
    });
    if (!hasRobotGeometryBounds || bbox.isEmpty()) return 0;
    const size = new THREE.Vector3();
    bbox.getSize(size);
    return Math.max(size.x, size.y, size.z) || 0;
  };

  modelGroup.updateWorldMatrix(true, true);
  const rootBodies = modelGroup.children.filter((child) => !!child?.userData?.isBodyContainer);
  return rootBodies.length > 0
    ? rootBodies.reduce((maxDim, root) => Math.max(maxDim, computeLargestDimFromNode(root)), 0)
    : computeLargestDimFromNode(modelGroup);
}
